"use client";

import {
  type CSSProperties,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { InfoDot } from "../data-display/InfoDot";
import {
  FormulaViewer,
  FormulaViewerButton,
  type FormulaViewerToken,
} from "../formula/FormulaViewer";
import { ChevR, Clock } from "../lib/icons";
import "./AssumptionSummaryRowsTable.css";

export interface AssumptionTableRow {
  id: string;
  label: string;
  description?: string;
  value: string | null;
  unresolved?: boolean;
  sourceLabel?: string;
  historyCount?: number;
}

export interface PendingCalculation {
  blockers: Array<{ id: string; label: string }>;
}

export type { FormulaViewerToken } from "../formula/FormulaViewer";

export interface AssumptionMetricGroup {
  id: string;
  label: string;
  description?: string;
  value: string | null;
  baselineValue?: string | null;
  sourceCount?: number;
  rows: AssumptionTableRow[];
  pendingCalculation?: PendingCalculation;
  formula?: FormulaViewerToken[];
  defaultExpanded?: boolean;
}

export interface AssumptionCadenceBucket {
  id: string;
  label: string;
  description?: string;
  value: string | null;
  groups: AssumptionMetricGroup[];
  pendingCalculation?: PendingCalculation;
}

export interface AssumptionSummaryRowsTableProps {
  buckets: AssumptionCadenceBucket[];
  onOpenEdit?: (rowId: string) => void;
  onOpenSource?: (rowId: string) => void;
  onOpenHistory?: (rowId: string) => void;
}

/**
 * Domain-neutral assumptions table for model inputs grouped by cadence and
 * calculated metric. Callers provide all display copy and formatted values.
 */
export function AssumptionSummaryRowsTable({
  buckets,
  onOpenEdit,
  onOpenSource,
  onOpenHistory,
}: AssumptionSummaryRowsTableProps) {
  const groups = useMemo(
    () => buckets.flatMap((bucket) => bucket.groups),
    [buckets],
  );

  const initialExpanded = useMemo(
    () =>
      new Set(
        groups
          .filter((group) => group.defaultExpanded)
          .map((group) => group.id),
      ),
    [groups],
  );
  const [expanded, setExpanded] = useState(initialExpanded);
  const [formulaGroupId, setFormulaGroupId] = useState<string | null>(null);
  const formulaGroup =
    groups.find((group) => group.id === formulaGroupId) ?? null;
  const formulaTriggerRef = useRef<HTMLButtonElement | null>(null);
  const seenGroupIds = useRef(new Set(groups.map((group) => group.id)));

  // Preserve a user's open/closed choices across data refreshes and section
  // changes. Only groups introduced after mount receive their default state;
  // unresolved groups therefore open by default without reopening a group the
  // user deliberately collapsed.
  useEffect(() => {
    const newGroups = groups.filter(
      (group) => !seenGroupIds.current.has(group.id),
    );
    if (newGroups.length > 0) {
      setExpanded((current) => {
        const next = new Set(current);
        for (const group of newGroups) {
          if (group.defaultExpanded) next.add(group.id);
        }
        return next;
      });
      for (const group of newGroups) seenGroupIds.current.add(group.id);
    }
  }, [groups]);

  function toggleGroup(group: AssumptionMetricGroup) {
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(group.id)) next.delete(group.id);
      else next.add(group.id);
      return next;
    });
  }

  function openFormula(
    group: AssumptionMetricGroup,
    trigger: HTMLButtonElement,
  ) {
    formulaTriggerRef.current = trigger;
    setFormulaGroupId(group.id);
  }

  function closeFormula() {
    setFormulaGroupId(null);
    requestAnimationFrame(() => formulaTriggerRef.current?.focus());
  }

  return (
    <div className="min-h-0 overflow-auto bg-(--bg-white)">
      <table className="w-full min-w-[710px] table-fixed border-collapse">
        <colgroup>
          <col className="w-auto" />
          <col className="w-[170px]" />
          <col className="w-[240px]" />
        </colgroup>
        <thead>
          <tr>
            {["Assumption", "Value", "Source / History"].map((label) => (
              <th
                key={label}
                className="sticky top-0 z-10 bg-(--bg-white) px-6 py-2.5 text-left text-[10.5px] font-bold uppercase tracking-[.09em] text-(--fg-muted) shadow-[inset_0_-1px_0_var(--divider-section)]"
              >
                {label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {buckets.map((bucket, index) => {
            const empty = bucket.groups.length === 0;
            return (
              <BucketRows
                key={bucket.id}
                bucket={bucket}
                empty={empty}
                first={index === 0}
                expanded={expanded}
                onToggleGroup={toggleGroup}
                onOpenEdit={onOpenEdit}
                onOpenSource={onOpenSource}
                onOpenHistory={onOpenHistory}
                onOpenFormula={openFormula}
              />
            );
          })}
        </tbody>
      </table>
      {formulaGroup && (
        <FormulaViewer
          title={formulaGroup.label}
          tokens={formulaGroup.formula}
          onClose={closeFormula}
          onOpenReference={
            onOpenSource
              ? (referenceId) => {
                  closeFormula();
                  onOpenSource(referenceId);
                }
              : undefined
          }
        />
      )}
    </div>
  );
}

function BucketRows({
  bucket,
  empty,
  first,
  expanded,
  onToggleGroup,
  onOpenEdit,
  onOpenSource,
  onOpenHistory,
  onOpenFormula,
}: {
  bucket: AssumptionCadenceBucket;
  empty: boolean;
  /** The first bucket sits directly under the sticky header, which already
      draws the divider — so it skips its own top border to avoid a doubled line. */
  first: boolean;
  expanded: Set<string>;
  onToggleGroup: (group: AssumptionMetricGroup) => void;
  onOpenEdit?: (rowId: string) => void;
  onOpenSource?: (rowId: string) => void;
  onOpenHistory?: (rowId: string) => void;
  onOpenFormula: (
    group: AssumptionMetricGroup,
    trigger: HTMLButtonElement,
  ) => void;
}) {
  const bucketTopBorder = first ? "" : "border-t border-(--divider-section) ";
  return (
    <>
      <tr className={empty ? "opacity-30" : undefined}>
        <td
          className={`${bucketTopBorder}bg-(--bg-white) px-6 pb-2 pt-[26px] align-middle`}
        >
          <span className="text-[13px] font-bold uppercase tracking-[.08em] text-(--fg-primary)">
            {bucket.label}
          </span>
          {bucket.description && (
            <span className="ml-2 text-[11.5px] text-(--fg-muted)">
              {bucket.description}
            </span>
          )}
        </td>
        <td
          className={`${bucketTopBorder}bg-(--bg-white) pb-2 pl-5 pr-6 pt-[26px] align-middle text-[13px] font-semibold text-(--fg-secondary)`}
        >
          {bucket.pendingCalculation ? (
            <PendingCalculationIndicator
              label={`${bucket.label} total`}
              pending={bucket.pendingCalculation}
            />
          ) : (
            (bucket.value ?? "—")
          )}
        </td>
        <td
          className={`${bucketTopBorder}bg-(--bg-white) px-6 pb-2 pt-[26px]`}
        />
      </tr>
      {bucket.groups.map((group) => {
        const isExpanded = expanded.has(group.id);
        return (
          <GroupRows
            key={group.id}
            group={group}
            expanded={isExpanded}
            onToggle={() => onToggleGroup(group)}
            onOpenEdit={onOpenEdit}
            onOpenSource={onOpenSource}
            onOpenHistory={onOpenHistory}
            onOpenFormula={onOpenFormula}
          />
        );
      })}
    </>
  );
}

function PendingCalculationIndicator({
  label,
  pending,
  warm = false,
}: {
  label: string;
  pending: PendingCalculation;
  warm?: boolean;
}) {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const tooltipId = useId();
  const [style, setStyle] = useState<CSSProperties | null>(null);
  const count = pending.blockers.length;

  function show() {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const maxWidth = Math.min(560, window.innerWidth - 24);
    const estimatedHeight = Math.min(260, 52 + count * 25);
    const below = rect.bottom + 8;
    setStyle({
      position: "fixed",
      left: Math.max(
        12,
        Math.min(rect.left, window.innerWidth - maxWidth - 12),
      ),
      top:
        below + estimatedHeight > window.innerHeight - 12
          ? Math.max(12, rect.top - estimatedHeight - 8)
          : below,
      zIndex: 9999,
    });
  }

  function hide() {
    setStyle(null);
  }

  function onKeyDown(event: React.KeyboardEvent<HTMLButtonElement>) {
    if (event.key === "Escape") {
      event.preventDefault();
      hide();
    }
  }

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        aria-label={`${label} is awaiting ${count} ${count === 1 ? "decision" : "decisions"}`}
        aria-describedby={style ? tooltipId : undefined}
        className={`as-pending-trigger${warm ? " as-pending-trigger-warm" : ""}`}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        onKeyDown={onKeyDown}
      >
        <span className="as-pending-dot" aria-hidden="true" />
      </button>
      {style &&
        createPortal(
          <div
            id={tooltipId}
            role="tooltip"
            className="as-pending-popover"
            style={style}
          >
            <span className="as-pending-popover-title">
              Needs a decision before this calculates
            </span>
            {pending.blockers.map((blocker) => (
              <span key={blocker.id} className="as-pending-popover-item">
                <span className="as-pending-popover-dot" aria-hidden="true" />
                <span>{blocker.label}</span>
              </span>
            ))}
          </div>,
          document.body,
        )}
    </>
  );
}

function GroupRows({
  group,
  expanded,
  onToggle,
  onOpenEdit,
  onOpenSource,
  onOpenHistory,
  onOpenFormula,
}: {
  group: AssumptionMetricGroup;
  expanded: boolean;
  onToggle: () => void;
  onOpenEdit?: (rowId: string) => void;
  onOpenSource?: (rowId: string) => void;
  onOpenHistory?: (rowId: string) => void;
  onOpenFormula: (
    group: AssumptionMetricGroup,
    trigger: HTMLButtonElement,
  ) => void;
}) {
  return (
    <>
      <tr className="group">
        <td className="border-y border-t-(--divider-section) border-b-(--divider-row) bg-(--bg-canvas) px-5 py-[10px] align-middle transition-colors duration-200 group-hover:bg-(--bg-focused)">
          <span className="flex min-w-0 items-center gap-1.5">
            <button
              type="button"
              aria-expanded={expanded}
              aria-label={`${expanded ? "Collapse" : "Expand"} ${group.label}`}
              className="inline-flex min-w-0 items-center gap-2 border-0 bg-transparent p-0 text-left font-[inherit] text-(--fg-primary)"
              onClick={onToggle}
            >
              <span
                className={`inline-flex size-5 shrink-0 items-center justify-center rounded-full border border-(--border-default) bg-(--bg-white) text-(--fg-muted) transition-transform duration-200 motion-reduce:transition-none ${expanded ? "rotate-90" : ""}`}
              >
                <ChevR size={13} />
              </span>
              <span className="truncate text-[14px] font-semibold">
                {group.label}
              </span>
            </button>
            {group.description && (
              <span className="shrink-0">
                <InfoDot text={group.description} />
              </span>
            )}
          </span>
        </td>
        <td className="border-y border-t-(--divider-section) border-b-(--divider-row) bg-(--bg-canvas) px-5 py-[10px] align-middle whitespace-nowrap transition-colors duration-200 group-hover:bg-(--bg-focused)">
          <span className="inline-flex items-center gap-[9px]">
            {group.baselineValue ? (
              <span className="inline-flex items-center gap-2">
                <span className="text-[13px] font-medium text-(--fg-muted)">
                  {group.baselineValue}
                </span>
                <span aria-hidden="true" className="text-(--fg-subtle)">
                  →
                </span>
                {group.pendingCalculation ? (
                  <PendingCalculationIndicator
                    label={group.label}
                    pending={group.pendingCalculation}
                    warm={!expanded}
                  />
                ) : (
                  <span className="text-[14px] font-bold text-(--fg-primary)">
                    {group.value ?? "—"}
                  </span>
                )}
              </span>
            ) : group.pendingCalculation ? (
              <PendingCalculationIndicator
                label={group.label}
                pending={group.pendingCalculation}
                warm={!expanded}
              />
            ) : (
              <span className="text-[14px] font-bold text-(--fg-primary)">
                {group.value ?? "—"}
              </span>
            )}
            <FormulaViewerButton
              label={group.label}
              onClick={(event) => {
                event.stopPropagation();
                onOpenFormula(group, event.currentTarget);
              }}
            />
          </span>
        </td>
        <td className="border-y border-t-(--divider-section) border-b-(--divider-row) bg-(--bg-canvas) px-5 py-[10px] align-middle text-[12px] text-(--fg-muted) transition-colors duration-200 group-hover:bg-(--bg-focused)">
          {group.sourceCount
            ? `${group.sourceCount} source${group.sourceCount === 1 ? "" : "s"}`
            : null}
        </td>
      </tr>
      {expanded &&
        group.rows.map((row) => (
          <tr key={row.id}>
            <td
              className={[
                "border-b px-6 py-[11px] pl-14 align-middle",
                row.unresolved
                  ? "border-(--decide-border) bg-(--decide-bg)"
                  : "border-(--divider-row)",
              ].join(" ")}
            >
              <span className="flex min-w-0 items-center gap-1.5">
                <span className="truncate text-[13.5px] text-(--fg-primary)">
                  {row.label}
                </span>
                {row.description && (
                  <span className="shrink-0">
                    <InfoDot text={row.description} />
                  </span>
                )}
              </span>
            </td>
            <td
              className={[
                "border-b px-6 py-[11px] align-middle whitespace-nowrap",
                row.unresolved
                  ? "border-(--decide-border) bg-(--decide-bg)"
                  : "border-(--divider-row)",
              ].join(" ")}
            >
              <button
                type="button"
                aria-label={`Edit value for ${row.label}`}
                className={[
                  "border-0 border-b border-dashed bg-transparent p-0 text-left text-[14px] font-semibold",
                  "hover:border-(--fg-brand) hover:text-(--fg-brand)",
                  row.unresolved
                    ? "border-(--decide) text-(--decide)"
                    : "border-(--border-default) text-(--fg-primary)",
                ].join(" ")}
                onClick={() => onOpenEdit?.(row.id)}
              >
                {row.value ?? (row.unresolved ? "Needs decision" : "—")}
              </button>
            </td>
            <td
              className={[
                "border-b px-6 py-[11px] align-middle",
                row.unresolved
                  ? "border-(--decide-border) bg-(--decide-bg)"
                  : "border-(--divider-row)",
              ].join(" ")}
            >
              <span className="flex min-w-0 items-center gap-2">
                {row.sourceLabel && onOpenSource && (
                  <button
                    type="button"
                    aria-label={`View source for ${row.label}`}
                    className="inline-block max-w-full truncate border-0 border-b border-dashed border-(--border-default) bg-transparent p-0 text-left text-[13.5px] text-(--fg-primary) hover:border-(--fg-brand) hover:text-(--fg-brand)"
                    onClick={() => onOpenSource(row.id)}
                  >
                    {row.sourceLabel}
                  </button>
                )}
                {onOpenHistory && (
                  <button
                    type="button"
                    aria-label={`View history for ${row.label}`}
                    title="View history"
                    className={[
                      "ml-auto inline-flex items-center gap-1 rounded-(--radius-xs) border-0 bg-transparent p-1 text-(--fg-subtle)",
                      "hover:bg-(--bg-hover) hover:text-(--fg-primary)",
                      row.historyCount ? "text-(--fg-secondary)" : "",
                    ].join(" ")}
                    onClick={() => onOpenHistory(row.id)}
                  >
                    <Clock size={15} />
                    {row.historyCount ? (
                      <span className="text-[12px] font-semibold">
                        {row.historyCount}
                      </span>
                    ) : null}
                  </button>
                )}
              </span>
            </td>
          </tr>
        ))}
    </>
  );
}

/** Retained for the legacy full-page assumptions layout. */
export function AssumptionTableLayout() {
  return null;
}
